/**
 * BulkExportBar Component
 *
 * Floating action bar that appears when sources are selected.
 * Provides bulk export and bulk labeling functionality.
 */

import { Download, X, CheckSquare, Tag, Lock } from "lucide-react";

interface BulkExportBarProps {
  selectedCount: number;
  onExport: () => void;
  onLabel: () => void;
  onClearSelection: () => void;
  canLabel: boolean;
}

export function BulkExportBar({
  selectedCount,
  onExport,
  onLabel,
  onClearSelection,
  canLabel,
}: BulkExportBarProps) {
  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
      <div className="bg-slate-800 text-white rounded-lg shadow-xl px-4 py-3 flex items-center gap-4">
        {/* Selection Count */}
        <div className="flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-amber-400" />
          <span className="font-medium">
            {selectedCount} source{selectedCount !== 1 ? "s" : ""} selected
          </span>
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-slate-600" />

        {/* Bulk Label Button */}
        <button
          onClick={onLabel}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium transition-colors ${
            canLabel
              ? "bg-slate-600 hover:bg-slate-500 text-white"
              : "bg-slate-700 text-slate-400 cursor-not-allowed"
          }`}
          title={canLabel ? "Label selected sources" : "Upgrade to Starter to label sources"}
        >
          {canLabel ? (
            <Tag className="w-4 h-4" />
          ) : (
            <Lock className="w-4 h-4" />
          )}
          Bulk Label
        </button>

        {/* Export Button */}
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg font-medium transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>

        {/* Clear Selection */}
        <button
          onClick={onClearSelection}
          className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
          title="Clear selection"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
